import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
    Modal, Form, Input, DatePicker, Select, Button, Progress, Tag, 
    Empty, Popconfirm, message, Tooltip, Badge, Skeleton 
} from "antd";
import { 
    PlusOutlined, SearchOutlined, CheckCircleOutlined, 
    ClockCircleOutlined, ExclamationCircleOutlined, 
    ArrowRightOutlined, DeleteOutlined, 
    CalendarOutlined, UserOutlined, FlagOutlined 
} from "@ant-design/icons";
import LoadingSpinner from "../../common/LoadingSpinner";
import "./ClientOnboarding.css";

const API_URL = process.env.REACT_APP_API_URL || "";

const INDUSTRY_OPTIONS = [
    "Technology & SaaS",
    "Financial Services & Banking",
    "Healthcare & Pharmaceuticals",
    "Insurance & InsurTech",
    "Retail & E-Commerce",
    "Energy & Utilities",
    "Telecommunications",
    "Government & Public Sector",
    "Other"
];

const OnboardingHub = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [trackers, setTrackers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [healthFilter, setHealthFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");

    // Modal state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form] = Form.useForm();

    const fetchTrackers = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}api/superadmin/onboarding/`, { withCredentials: true });
            if (res.data?.success) {
                setTrackers(res.data.trackers || []);
            }
        } catch (err) {
            console.error("Failed to fetch onboarding trackers:", err);
            message.error(err.response?.data?.message || "Failed to load onboarding trackers.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrackers();
    }, []);

    const handleCreateSubmit = async (values) => {
        try {
            setCreating(true);
            const payload = {
                client_name: values.client_name,
                legal_entity_name: values.legal_entity_name || values.client_name,
                client_email: values.client_email,
                client_phone: values.client_phone,
                industry: values.industry,
                lead_owner: values.lead_owner || "Implementation Lead",
                vrm_assigned: values.vrm_assigned || "Unassigned",
                target_go_live_date: values.target_go_live_date ? values.target_go_live_date.format("YYYY-MM-DD") : null,
                hypercare_end_date: values.hypercare_end_date ? values.hypercare_end_date.format("YYYY-MM-DD") : null,
            };

            const res = await axios.post(`${API_URL}api/superadmin/onboarding/`, payload, { withCredentials: true });
            if (res.data?.success) {
                message.success("Client onboarding tracker initialized!");
                setIsCreateModalOpen(false);
                form.resetFields();
                // Navigate directly to the new tracker workspace
                const newId = res.data.tracker?._id;
                if (newId) {
                    navigate(`/superadmin_onboarding/${newId}`);
                } else {
                    fetchTrackers();
                }
            }
        } catch (err) {
            console.error("Error creating tracker:", err);
            message.error(err.response?.data?.message || "Failed to create tracker.");
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id, e) => {
        e?.stopPropagation();
        try {
            const res = await axios.delete(`${API_URL}api/superadmin/onboarding/${id}/`, { withCredentials: true });
            if (res.data?.success) {
                message.success("Onboarding tracker removed.");
                setTrackers(prev => prev.filter(t => t._id !== id));
            }
        } catch (err) {
            message.error(err.response?.data?.message || "Failed to delete tracker.");
        }
    };

    // Filtered trackers
    const filteredTrackers = useMemo(() => {
        return trackers.filter(t => {
            const matchSearch = (
                (t.client_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (t.tracker_code || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (t.lead_owner || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
            const matchHealth = healthFilter === "all" || (t.health || "on_track") === healthFilter;
            const matchStatus = statusFilter === "all" || (t.status || "in_progress") === statusFilter;
            return matchSearch && matchHealth && matchStatus;
        });
    }, [trackers, searchTerm, healthFilter, statusFilter]);

    // Summary counts
    const metrics = useMemo(() => {
        const total = trackers.length;
        const inProgress = trackers.filter(t => t.status === "in_progress").length;
        const delayed = trackers.filter(t => t.health === "delayed" || (t.delays && t.delays.length > 0)).length;
        const completed = trackers.filter(t => t.status === "completed" || t.status === "go_live_approved").length;
        return { total, inProgress, delayed, completed };
    }, [trackers]);

    const getCurrentStageName = (tracker) => {
        const stages = tracker.stages || [];
        const inProg = stages.find(s => s.status === "in_progress");
        if (inProg) return `Stage ${inProg.id}: ${inProg.name}`;
        const lastCompleted = [...stages].reverse().find(s => s.status === "completed");
        if (lastCompleted) return `Stage ${lastCompleted.id}: ${lastCompleted.name}`;
        return stages[0] ? `Stage 1: ${stages[0].name}` : "Not Started";
    };

    return (
        <div className="onb-container">
            {/* Header Card */}
            <div className="onb-header-card">
                <div className="onb-title-row">
                    <div>
                        <h1 className="onb-title">
                            <i className="fas fa-clipboard-check text-primary"></i> Client Onboarding Management
                        </h1>
                        <p className="onb-subtitle">
                            Independent structured lifecycle tracker & operational diary for new enterprise clients.
                        </p>
                    </div>
                    <Button 
                        type="primary" 
                        size="large" 
                        icon={<PlusOutlined />} 
                        onClick={() => setIsCreateModalOpen(true)}
                        style={{ background: "#2563eb", borderColor: "#2563eb", borderRadius: "8px", fontWeight: "600" }}
                    >
                        New Client Onboarding
                    </Button>
                </div>

                {/* Metric Summary Cards */}
                <div className="onb-metrics-grid">
                    <div className="onb-metric-card">
                        <div className="onb-metric-icon blue">
                            <i className="fas fa-layer-group"></i>
                        </div>
                        <div>
                            <div className="onb-metric-val">{metrics.total}</div>
                            <div className="onb-metric-label">Total Onboardings</div>
                        </div>
                    </div>
                    <div className="onb-metric-card">
                        <div className="onb-metric-icon amber">
                            <i className="fas fa-spinner"></i>
                        </div>
                        <div>
                            <div className="onb-metric-val">{metrics.inProgress}</div>
                            <div className="onb-metric-label">In Progress</div>
                        </div>
                    </div>
                    <div className="onb-metric-card">
                        <div className="onb-metric-icon red">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <div>
                            <div className="onb-metric-val">{metrics.delayed}</div>
                            <div className="onb-metric-label">Delayed / At Risk</div>
                        </div>
                    </div>
                    <div className="onb-metric-card">
                        <div className="onb-metric-icon green">
                            <i className="fas fa-rocket"></i>
                        </div>
                        <div>
                            <div className="onb-metric-val">{metrics.completed}</div>
                            <div className="onb-metric-label">Go-Live / Completed</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toolbar Filters */}
            <div className="onb-toolbar">
                <div className="onb-toolbar-left">
                    <Input 
                        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                        placeholder="Search by client name, code, or lead owner..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: 320, borderRadius: 8 }}
                        allowClear
                    />
                    <Select 
                        value={healthFilter} 
                        onChange={setHealthFilter} 
                        style={{ width: 160 }}
                        options={[
                            { label: "All Health", value: "all" },
                            { label: "🟢 On Track", value: "on_track" },
                            { label: "🟡 At Risk", value: "at_risk" },
                            { label: "🔴 Delayed", value: "delayed" },
                        ]}
                    />
                    <Select 
                        value={statusFilter} 
                        onChange={setStatusFilter} 
                        style={{ width: 180 }}
                        options={[
                            { label: "All Statuses", value: "all" },
                            { label: "In Progress", value: "in_progress" },
                            { label: "Go-Live Approved", value: "go_live_approved" },
                            { label: "Completed", value: "completed" },
                        ]}
                    />
                </div>
                <div style={{ color: "#64748b", fontSize: 13, fontWeight: 500 }}>
                    Showing {filteredTrackers.length} client{filteredTrackers.length === 1 ? "" : "s"}
                </div>
            </div>

            {/* Client Cards Grid */}
            {loading ? (
                <div className="onb-cards-grid">
                    {[...Array(6)].map((_, idx) => (
                        <div key={idx} className="onb-client-card" style={{ minHeight: 220 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                                <Skeleton.Button active size="small" style={{ width: 85 }} />
                                <Skeleton.Button active size="small" style={{ width: 65 }} />
                            </div>
                            <Skeleton.Input active size="default" style={{ width: "75%", height: 22, marginBottom: 8 }} />
                            <Skeleton.Input active size="small" style={{ width: "50%", height: 16, marginBottom: 16 }} />
                            <Skeleton active paragraph={{ rows: 2 }} title={false} />
                        </div>
                    ))}
                </div>
            ) : filteredTrackers.length === 0 ? (
                <div style={{ background: "#ffffff", padding: "60px 20px", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                    <Empty 
                        description={searchTerm || healthFilter !== "all" || statusFilter !== "all" ? "No clients match your filter criteria." : "No client onboarding trackers created yet."}
                    >
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalOpen(true)}>
                            Create First Client Tracker
                        </Button>
                    </Empty>
                </div>
            ) : (
                <div className="onb-cards-grid">
                    {filteredTrackers.map((t) => {
                        const progress = t.overall_progress || 0;
                        const health = t.health || "on_track";
                        const hasDelays = t.delays && t.delays.length > 0;

                        return (
                            <div key={t._id} className="onb-client-card">
                                <div>
                                    <div className="onb-client-card-top">
                                        <span className="onb-client-code">{t.tracker_code}</span>
                                        <div>
                                            {health === "delayed" || hasDelays ? (
                                                <Tag color="error">Delayed</Tag>
                                            ) : health === "at_risk" ? (
                                                <Tag color="warning">At Risk</Tag>
                                            ) : (
                                                <Tag color="success">On Track</Tag>
                                            )}
                                            {t.status === "completed" && <Tag color="blue">Completed</Tag>}
                                        </div>
                                    </div>

                                    <h3 className="onb-client-name">{t.client_name}</h3>
                                    <div className="onb-client-meta">
                                        <span><i className="fas fa-building"></i> {t.industry || "Enterprise"}</span>
                                        <span style={{ margin: "0 8px" }}>•</span>
                                        <span><UserOutlined /> {t.lead_owner || "Lead"}</span>
                                    </div>

                                    <div className="onb-stage-badge">
                                        <ClockCircleOutlined /> {getCurrentStageName(t)}
                                    </div>

                                    {/* Progress */}
                                    <div className="onb-progress-box">
                                        <div className="onb-progress-labels">
                                            <span>Progress</span>
                                            <span>{progress}% ({t.completed_tasks || 0}/{t.total_tasks || 33} tasks)</span>
                                        </div>
                                        <Progress 
                                            percent={progress} 
                                            size="small" 
                                            strokeColor={progress === 100 ? "#16a34a" : (health === "delayed" ? "#ef4444" : "#2563eb")}
                                            showInfo={false}
                                        />
                                    </div>

                                    {/* Delay Warning if applicable */}
                                    {hasDelays && (
                                        <div style={{ background: "#fef2f2", color: "#b91c1c", padding: "8px 12px", borderRadius: 6, fontSize: 12, marginBottom: 12, border: "1px solid #fee2e2" }}>
                                            <i className="fas fa-exclamation-triangle"></i> Delay: {t.delays[t.delays.length - 1].reason_category}
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="onb-card-footer">
                                    <div style={{ fontSize: 12, color: "#64748b" }}>
                                        <CalendarOutlined /> Target: {t.target_go_live_date || "Not set"}
                                    </div>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <Popconfirm
                                            title="Delete Tracker"
                                            description="Are you sure you want to delete this onboarding tracker?"
                                            onConfirm={(e) => handleDelete(t._id, e)}
                                            okText="Yes, Delete"
                                            cancelText="Cancel"
                                            okButtonProps={{ danger: true }}
                                        >
                                            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                                        </Popconfirm>
                                        <Link to={`/superadmin_onboarding/${t._id}`}>
                                            <Button type="primary" size="small" style={{ background: "#2563eb" }}>
                                                Open Tracker <ArrowRightOutlined />
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create New Onboarding Tracker Modal */}
            <Modal
                title={
                    <div style={{ fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                        <i className="fas fa-plus-circle text-primary"></i> Create Client Onboarding Tracker
                    </div>
                }
                open={isCreateModalOpen}
                onCancel={() => setIsCreateModalOpen(false)}
                footer={null}
                width={650}
                destroyOnClose
            >
                <p style={{ color: "#64748b", fontSize: 13, marginTop: -8, marginBottom: 20 }}>
                    Initializes a standalone 11-stage onboarding lifecycle tracker with a standard enterprise checklist.
                </p>

                <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <Form.Item 
                            name="client_name" 
                            label="Client / Company Name" 
                            rules={[{ required: true, message: "Please enter client name" }]}
                        >
                            <Input placeholder="e.g. Acme Global Technologies" />
                        </Form.Item>

                        <Form.Item name="legal_entity_name" label="Legal Entity Name (Optional)">
                            <Input placeholder="e.g. Acme Global Inc." />
                        </Form.Item>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <Form.Item name="client_email" label="Primary Contact Email">
                            <Input type="email" placeholder="e.g. admin@acmeglobal.com" />
                        </Form.Item>

                        <Form.Item name="client_phone" label="Primary Contact Phone">
                            <Input placeholder="e.g. +1 (555) 234-5678" />
                        </Form.Item>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <Form.Item name="industry" label="Industry Sector" initialValue="Technology & SaaS">
                            <Select options={INDUSTRY_OPTIONS.map(i => ({ label: i, value: i }))} />
                        </Form.Item>

                        <Form.Item name="lead_owner" label="Internal Implementation Lead" initialValue="Implementation Lead">
                            <Input placeholder="e.g. Alex Johnson (Lead IPM)" />
                        </Form.Item>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <Form.Item name="target_go_live_date" label="Target Go-Live Date">
                            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                        </Form.Item>

                        <Form.Item name="vrm_assigned" label="Assigned VRM Lead" initialValue="Unassigned">
                            <Input placeholder="e.g. Sarah Miller (Risk Lead)" />
                        </Form.Item>
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
                        <Button onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={creating} style={{ background: "#2563eb", borderColor: "#2563eb" }}>
                            Create Tracker Instance
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default OnboardingHub;
